// A list of bare scalar values (strings / numbers / booleans) — distinct from
// the enum-backed multi_* types and from `upload`. Renders one input per item
// with add / remove controls, coercing each item to `config.item_type`. Rows
// are React-keyed by a uuid kept in a ref (the stored values are bare scalars
// and can't carry a __key), spliced in lockstep with the value array so keys
// stay stable across add / remove.

import { useRef } from 'react'
import { captionText } from '../caption'
import { FieldShell } from '../FieldShell'
import { useFieldContext, type FieldComponentProps } from '../rendererContext'
import type { ScalarItemType, ScalarListConfig } from '../types'
import { errorIdFor } from '../ids'
import { newRowKey } from '../value'
import { useControlMeta } from './useControlMeta'

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

/** Coerce a text-input string into the configured item type. */
function coerceItem(raw: string, itemType: ScalarItemType | undefined): unknown {
  if (itemType === 'integer' || itemType === 'number') {
    if (raw.trim() === '') return ''
    const n = Number(raw)
    return Number.isNaN(n) ? raw : n
  }
  return raw
}

/** The value a freshly added row starts with. */
function emptyItem(itemType: ScalarItemType | undefined): unknown {
  return itemType === 'boolean' ? false : ''
}

function displayString(value: unknown): string {
  return value == null ? '' : String(value)
}

export function ScalarListField({ field, path, value, errors }: FieldComponentProps) {
  const meta = useControlMeta(field, path, errors)
  const { errors: allErrors } = useFieldContext()
  const config = field.config as ScalarListConfig | undefined
  const itemType = config?.item_type
  const items = asArray(value)

  const min = typeof config?.min_items === 'number' ? config.min_items : 0
  const max = typeof config?.max_items === 'number' ? config.max_items : Infinity
  const interactive = !meta.disabled
  const canAdd = interactive && items.length < max
  const canRemove = interactive && items.length > min

  // Stable per-row keys; the stored items are bare scalars, so keys live here.
  const keysRef = useRef<string[]>([])
  while (keysRef.current.length < items.length) keysRef.current.push(newRowKey())
  if (keysRef.current.length > items.length) keysRef.current.length = items.length

  if (field.display_only) {
    return (
      <FieldShell field={field} path={path} errors={errors} groupLabel>
        <ul id={meta.id} className="list-unstyled mb-0">
          {items.map((item, i) => (
            <li key={keysRef.current[i]}>{displayString(item)}</li>
          ))}
        </ul>
      </FieldShell>
    )
  }

  const updateItem = (index: number, next: unknown) => {
    const nextItems = items.slice()
    nextItems[index] = next
    meta.setFieldValue(path, nextItems)
  }

  const addItem = () => {
    meta.setFieldValue(path, [...items, emptyItem(itemType)])
  }

  const removeItem = (index: number) => {
    keysRef.current.splice(index, 1)
    meta.setFieldValue(
      path,
      items.filter((_, i) => i !== index),
    )
    meta.notifyBlur(path)
  }

  return (
    <FieldShell field={field} path={path} errors={errors} groupLabel>
      <div role="group" id={meta.id} aria-labelledby={meta.labelId} aria-describedby={meta.describedBy}>
        {items.map((item, i) => {
          const itemPath = `${path}[${i}]`
          const itemErrors = allErrors[itemPath] ?? []
          const rowLabel = field.label
            ? `${captionText(field, field.label)} ${i + 1}`
            : `item ${i + 1}`
          return (
            <div className="input-group mb-2" key={keysRef.current[i]}>
              {itemType === 'boolean' ? (
                // Input nested inside a <label> so the RX forms-universal
                // checkbox styling applies (see BooleanField).
                <label className="input-group-text mb-0">
                  <input
                    type="checkbox"
                    className={`mt-0${itemErrors.length ? ' is-invalid' : ''}`}
                    aria-label={rowLabel}
                    checked={item === true}
                    disabled={meta.disabled}
                    onChange={(event) => updateItem(i, event.target.checked)}
                    onBlur={() => meta.notifyBlur(path)}
                  />
                </label>
              ) : (
                <input
                  type={itemType === 'integer' || itemType === 'number' ? 'number' : 'text'}
                  className={`form-control${itemErrors.length ? ' is-invalid' : ''}`}
                  aria-label={rowLabel}
                  aria-invalid={itemErrors.length ? true : undefined}
                  aria-describedby={itemErrors.length ? errorIdFor(itemPath) : undefined}
                  value={displayString(item)}
                  placeholder={config?.placeholder}
                  disabled={meta.disabled}
                  onChange={(event) => updateItem(i, coerceItem(event.target.value, itemType))}
                  onBlur={() => meta.notifyBlur(path)}
                />
              )}
              <button
                type="button"
                className="btn btn-outline-secondary"
                aria-label={`Remove ${rowLabel}`}
                disabled={!canRemove}
                onClick={() => removeItem(i)}
              >
                Remove
              </button>
              {itemErrors.length ? (
                <div id={errorIdFor(itemPath)} className="invalid-feedback d-block">
                  {itemErrors.map((error, j) => (
                    <div key={`${error.code}-${j}`}>{error.message}</div>
                  ))}
                </div>
              ) : null}
            </div>
          )
        })}
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          disabled={!canAdd}
          onClick={addItem}
        >
          {config?.placeholder ? `Add ${config.placeholder}` : 'Add item'}
        </button>
      </div>
    </FieldShell>
  )
}
